import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/authService';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [resetComplete, setResetComplete] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Handle PKCE code exchange and PASSWORD_RECOVERY event
  useEffect(() => {
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
        setCheckingSession(false);
      }
    });

    const handleRecovery = async () => {
      // PKCE flow: exchange the code from the URL for a session
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!error) {
          setSessionReady(true);
          setCheckingSession(false);
          // Clean the code from the URL
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      }

      // Fallback: check if we already have a valid session
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session) {
        setSessionReady(true);
      }
      setCheckingSession(false);
    };

    handleRecovery();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd?.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('At least one lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('At least one number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('At least one special character');
    return errors;
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    setValidationErrors(validatePassword(value));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pwdErrors = validatePassword(password);
    if (pwdErrors?.length > 0) {
      setValidationErrors(pwdErrors);
      setError('Please fix the password requirements below');
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    try {
      setLoading(true);
      const result = await authService?.updatePassword(password);

      if (result?.error) {
        setError(result?.error?.message || t('resetPassword.errorMessage'));
        return;
      }

      setResetComplete(true);

      // Sign out after password reset so they can sign in fresh
      await supabase?.auth?.signOut();
    } catch (err) {
      console.error('Reset password error:', err);
      setError(t('resetPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const getRequirementStatus = (requirement) => {
    if (!password) return 'neutral';
    return validationErrors?.includes(requirement) ? 'invalid' : 'valid';
  };

  const requirements = [
    'At least 12 characters',
    'At least one uppercase letter',
    'At least one lowercase letter',
    'At least one number',
    'At least one special character'
  ];

  // Loading state while checking for recovery session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="pt-4 min-h-screen flex items-center justify-center px-4 py-12">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // No valid recovery session
  if (!sessionReady && !resetComplete) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="pt-4 min-h-screen flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-8 text-center">
              <div className="w-16 h-16 bg-warning/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-warning" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Invalid or Expired Link</h1>
              <p className="text-muted-foreground mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('resetPassword.submitButton')}
                </Button>
                <Link
                  to="/user-sign-in"
                  className="block text-center text-sm text-primary hover:underline"
                >
                  {t('resetPassword.backToSignIn')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />

      <div className="pt-4 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-8">
            {resetComplete ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} className="text-success" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">{t('resetPassword.title')}</h1>
                <p className="text-muted-foreground mb-6">
                  {t('resetPassword.successMessage')}
                </p>
                <Button
                  className="w-full"
                  onClick={() => navigate('/user-sign-in')}
                >
                  {t('resetPassword.backToSignIn')}
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Icon name="Lock" size={32} className="text-primary" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">Set New Password</h1>
                  <p className="text-muted-foreground">
                    Enter your new password below. Make sure it's strong and secure.
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-error/10 border border-error rounded-lg flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                    <span className="text-sm text-error">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => handlePasswordChange(e?.target?.value)}
                        placeholder="Enter new password"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e?.target?.value);
                          if (error) setError('');
                        }}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="mt-1 text-sm text-error">Passwords do not match</p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-sm font-medium text-foreground mb-2">Password Requirements</p>
                    <ul className="space-y-1">
                      {requirements?.map((req) => {
                        const status = getRequirementStatus(req);
                        return (
                          <li key={req} className="flex items-center gap-2 text-sm">
                            <Icon
                              name={status === 'valid' ? 'CheckCircle' : status === 'invalid' ? 'XCircle' : 'Circle'}
                              size={14}
                              className={
                                status === 'valid' ? 'text-success' :
                                status === 'invalid' ? 'text-error' :
                                'text-muted-foreground'
                              }
                            />
                            <span className={
                              status === 'valid' ? 'text-success' :
                              status === 'invalid' ? 'text-error' :
                              'text-muted-foreground'
                            }>
                              {req}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || validationErrors?.length > 0 || password !== confirmPassword || !password}
                    loading={loading}
                  >
                    {loading ? 'Updating...' : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
